import React, { useState, useEffect } from "react";
import apiService from "../services/api";

interface StorageUsageProps {
  showDetails?: boolean;
  className?: string;
}

interface StorageInfo {
  current_plan: {
    id: string;
    name: string;
    max_resumes?: number;
    storage_mb?: number; // Legacy field
    description: string;
  };
  usage: {
    resume_count?: number;
    usage_percentage: number;
    remaining_resumes?: number;
    is_unlimited?: boolean;
    // Legacy storage fields for backward compatibility
    total_size_bytes: number;
    total_size_mb: number;
    file_count: number;
    remaining_bytes?: number;
    remaining_mb?: number;
  };
  limits: {
    resume_limit?: number;
    is_unlimited?: boolean;
    // Legacy storage fields for backward compatibility
    storage_limit_bytes: number;
    storage_limit_mb: number;
  };
}

const getSafePercentage = (percentage: number | undefined): number => {
  if (typeof percentage !== "number" || !Number.isFinite(percentage)) {
    return 0;
  }

  return Math.min(Math.max(percentage, 0), 100);
};

const StorageUsage: React.FC<StorageUsageProps> = ({
  showDetails = true,
  className = "",
}) => {
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStorageInfo();
  }, []);

  const loadStorageInfo = async () => {
    try {
      setLoading(true);
      const data = await apiService.getStorageInfo();
      setStorageInfo(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load storage information");
    } finally {
      setLoading(false);
    }
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage >= 90) return "bg-red-600";
    if (percentage >= 75) return "bg-orange-500";
    if (percentage >= 50) return "bg-yellow-500";
    if (percentage >= 25) return "bg-green-500";
    return "bg-green-400";
  };

  const getUsageTextColor = (percentage: number): string => {
    if (percentage >= 90) return "text-red-600";
    if (percentage >= 75) return "text-orange-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-green-600";
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
          <div className="h-2 bg-gray-200 rounded mb-2"></div>
          {showDetails && <div className="h-3 bg-gray-200 rounded w-24"></div>}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border p-6 ${className}`}>
        <div className="text-red-600 text-sm">
          <svg
            className="w-4 h-4 inline mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      </div>
    );
  }

  if (!storageInfo) return null;

  const { usage, limits, current_plan } = storageInfo;
  const usagePercentage = getSafePercentage(usage.usage_percentage);
  const resumeCount = usage.resume_count ?? usage.file_count ?? 0;

  // Minimal dashboard view
  if (!showDetails) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border p-6 ${className}`}>
        <div>
          <p className="text-sm font-medium text-gray-500 mb-4">
            {" "}
            Resume count
          </p>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-indigo-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path
                    fillRule="evenodd"
                    d="M4 5a2 2 0 012-2v1a1 1 0 00-1 1v6a1 1 0 001 1v1a2 2 0 01-2-2V5zM14 5a2 2 0 012 2v6a2 2 0 01-2 2v-1a1 1 0 001-1V6a1 1 0 00-1-1V5z"
                    clipRule="evenodd"
                  />
                  <path d="M5 8a1 1 0 011-1h6a1 1 0 110 2H6a1 1 0 01-1-1zM5 10a1 1 0 011-1h6a1 1 0 110 2H6a1 1 0 01-1-1zM5 12a1 1 0 011-1h6a1 1 0 110 2H6a1 1 0 01-1-1z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p
                className={`text-2xl font-semibold ${getUsageTextColor(
                  usagePercentage,
                )}`}
              >
                {resumeCount}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Detailed view for other pages
  return (
    <div className={`bg-white rounded-lg shadow-sm border p-4 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-900">Resume Usage</h3>
        <span
          className={`text-xs font-medium ${getUsageTextColor(
            usagePercentage,
          )}`}
        >
          {usage.is_unlimited ? "Unlimited" : `${usagePercentage.toFixed(1)}%`}
        </span>
      </div>

      {/* Progress Bar */}
      {!usage.is_unlimited && (
        <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(
              usagePercentage,
            )}`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          ></div>
        </div>
      )}

      {/* Resume Count Details */}
      <div className="space-y-2 text-xs text-gray-600">
        <div className="flex justify-between">
          <span>Resumes Used:</span>
          <span className="font-medium">
            {resumeCount}
          </span>
        </div>
        {!usage.is_unlimited && (
          <>
            <div className="flex justify-between">
              <span>Resume Limit:</span>
              <span className="font-medium">
                {limits.resume_limit || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Remaining:</span>
              <span className="font-medium text-green-600">
                {usage.remaining_resumes !== undefined
                  ? usage.remaining_resumes
                  : "N/A"}
              </span>
            </div>
          </>
        )}

        <hr className="border-gray-200" />
        <div className="flex justify-between">
          <span>Plan:</span>
          <span className="font-medium text-indigo-600">
            {current_plan.name}
          </span>
        </div>
        {usage.is_unlimited && (
          <div className="flex justify-between">
            <span>Limit:</span>
            <span className="font-medium text-green-600">Unlimited</span>
          </div>
        )}
      </div>

      {/* Warning Messages */}
      {!usage.is_unlimited && usagePercentage >= 90 && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          <svg
            className="w-3 h-3 inline mr-1"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Resume limit is {usagePercentage.toFixed(1)}% full! Consider upgrading
          your plan.
        </div>
      )}

      {!usage.is_unlimited && usagePercentage >= 75 && usagePercentage < 90 && (
        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
          <svg
            className="w-3 h-3 inline mr-1"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Resume limit is {usagePercentage.toFixed(1)}% full. Monitor usage
          closely.
        </div>
      )}
    </div>
  );
};

export default StorageUsage;
